/**
 * @fileoverview POD Adjudication — shared UI types, labels, and helpers.
 *
 * Purpose: Types mirroring the `podClaims` / `podClaimItems` Convex tables,
 * label + badge-colour maps for statuses and recommendations, currency
 * formatters, and the derived adjudication-outcome helper shared by the
 * Recoveries phase and the creditors table POD columns.
 */

import type { Id } from "@cvx/_generated/dataModel";
import type { BadgeColor } from "@/ui/primitives/badge";
import type { RelatedPartyMatchUI } from "../_shared/relatedPartyShared";

// ---------------------------------------------------------------------------
// Types (from podClaims / podClaimItems DB tables)
// ---------------------------------------------------------------------------

export type PodClaimStatus =
  "not_requested" | "requested" | "received" | "adjudicated";

export type PodCreditorType = "unsecured" | "secured" | "priority" | "employee";

export type PodSupportingDocType =
  | "creditor_ledger"
  | "invoices"
  | "contracts"
  | "running_account_statement"
  | "other";

export type PodRecommendation =
  "admit" | "reject" | "hold" | "more_information";

export type PodClaimItemKind = "claimed" | "substantiated";

export interface PodClaimUI {
  _id: Id<"podClaims">;
  _creationTime: number;
  jobId: Id<"jobs">;
  liabilityEntryId?: Id<"liabilityEntries">;
  creditorName: string;
  creditorReference?: string;
  creditorType: PodCreditorType;
  status: PodClaimStatus;
  requestedAt?: number;
  receivedAt?: number;
  podDate?: string;
  claimedNet?: number;
  claimedGst?: number;
  claimedTotal: number;
  /** The creditor's own stated value of their security, off the Form 535. */
  securityHeldValue?: number;
  securityHeldDescription?: string;
  substantiatedTotal: number;
  /**
   * What the creditors table records as owing to this creditor.
   * Decorated onto the row by `listPodClaimsForJob`, so it is present on
   * list rows and absent on the claim document itself.
   */
  estimatedValue?: number;
  /**
   * Whether the rows behind `estimatedValue` were recorded independently of
   * this claim (`books`), written from the creditor's own proof of debt
   * (`pod`), or a mix. A `pod` estimate is the claim read back out of the
   * creditors table, so it must never be presented as a second opinion on it.
   */
  estimatedValueSource?: "books" | "pod" | "mixed";
  /**
   * The related party this creditor was matched to, when the Related Parties
   * register knows them. Joined onto the row by the panel — it is derived from
   * the register, not stored on the claim, so it follows the register as it
   * fills out over the life of the job.
   */
  relatedParty?: RelatedPartyMatchUI;
  supportingDocTypes: PodSupportingDocType[];
  supportingDocOther?: string;
  podExecuted?: boolean;
  correctlyWitnessed?: boolean;
  correctlyCompleted?: boolean;
  recommendation?: PodRecommendation;
  admittedAmount?: number;
  rejectedAmount?: number;
  groundsForRejection?: string;
  adjudicatedAt?: number;
  adjudicatedBy?: string;
  sourceDocuments: Array<{ documentId: string; fileName: string }>;
  notes: string;
  changeHistory: Array<{
    timestamp: number;
    actor: string;
    userId?: Id<"users">;
    action: string;
    reason: string;
    previousValues?: Record<string, unknown>;
    newValues?: Record<string, unknown>;
  }>;
}

export interface PodClaimItemUI {
  _id: Id<"podClaimItems">;
  jobId: Id<"jobs">;
  claimId: Id<"podClaims">;
  kind: PodClaimItemKind;
  date?: string;
  reference?: string;
  description: string;
  amount: number;
  /** Set when the line was auto-ingested from a classified POD document. */
  sourceDocumentId?: string;
  /** Set when a practitioner cited a job document on a line they typed. */
  attachedDocumentId?: string;
}

export interface PodReconciliationUI {
  claimedTotal: number;
  substantiatedTotal: number;
  variance: number;
  matched: boolean;
  postAppointmentAmount: number;
  postAppointmentItemCount: number;
  suggestedAdmit: number;
  suggestedReject: number;
}

/** One bank payment matched to the claim's creditor. */
export interface PodCrossCheckPaymentUI {
  date: string;
  description: string;
  amount: number;
}

/**
 * Independent verification against the COMPANY's records — the creditors
 * ledger and the bank statements — as opposed to the creditor's own
 * paperwork.
 */
/** A creditors-table row matched to the claim, with its provenance. */
export interface PodLedgerRowUI {
  id: string;
  creditorName: string;
  amount: number;
  source: string;
  natureOfDebt: string;
  notes?: string;
  sourceDocuments: Array<{ documentId: string; fileName: string }>;
  /** True when this row was written from a proof of debt, not our records. */
  derivedFromPod: boolean;
}

export interface PodCrossCheckUI {
  ledgerAmount: number | null;
  ledgerRowCount: number;
  ledgerVariance: number | null;
  ledgerRows: PodLedgerRowUI[];
  podDerivedRowCount: number;
  podDerivedAmount: number;
  payments: PodCrossCheckPaymentUI[];
  paymentsTotal: number;
  creditedOnClaim: number;
  uncreditedPayments: number;
  windowFrom: string | null;
  windowTo: string | null;
}

export type PodPaymentStatus =
  "credited" | "partially_credited" | "settles_line" | "unaccounted";

/** A bank payment classified against the claim. */
export interface PodReconciledPaymentUI extends PodCrossCheckPaymentUI {
  status: PodPaymentStatus;
  /** How much of this payment the claim's own credits account for. */
  creditedAmount: number;
  matchedLabel?: string;
  matchedLineId?: string;
}

export interface PodPaymentSummaryUI {
  credited: number;
  settlesLine: number;
  unaccounted: number;
  total: number;
}

/** One claim line beside the bank payment it corresponds to. */
export interface PodClaimPaymentRowUI {
  lineId: string;
  date?: string;
  description: string;
  amount: number;
  isCredit: boolean;
  payment?: PodCrossCheckPaymentUI;
  match?: "credit_matched" | "invoice_settled";
}

export interface PodClaimVsPaymentsUI {
  rows: PodClaimPaymentRowUI[];
  unmatchedPayments: PodCrossCheckPaymentUI[];
  paidTotal: number;
  matchedTotal: number;
  unmatchedTotal: number;
}

/** Where the substantiating evidence actually came from. */
export interface PodEvidenceUI {
  hasSubstantiation: boolean;
  /** At least one substantiating line came from a document the claim did not. */
  substantiationIsIndependent: boolean;
  /** Every substantiating line came from a claim document — not corroboration. */
  sharesSourceWithClaim: boolean;
  claimedDocIds: string[];
  substantiatedDocIds: string[];
}

export interface PodClaimDetailUI {
  claim: PodClaimUI;
  claimedItems: PodClaimItemUI[];
  substantiatedItems: PodClaimItemUI[];
  reconciliation: PodReconciliationUI;
  crossCheck?: PodCrossCheckUI;
  /** Line item id → the bank payment that appears to have settled it. */
  paymentMatches?: Record<string, PodCrossCheckPaymentUI>;
  /** Every bank payment, classified against the claim. */
  reconciledPayments?: PodReconciledPaymentUI[];
  paymentSummary?: PodPaymentSummaryUI;
  claimVsPayments?: PodClaimVsPaymentsUI;
  evidence?: PodEvidenceUI;
  appointmentDate: string | null;
}

/**
 * A drafted adjudication returned by
 * `jobs.recoveries.adjudicationDraft.draftAdjudication`.
 *
 * Proposals only — the action writes nothing. Each `*Basis` field is the
 * model's stated reason, shown beside the field it filled so the
 * practitioner can check the proposal rather than take it on trust.
 */
export interface AdjudicationDraft {
  supportingDocTypes: string[];
  supportingDocOther: string;
  supportingDocBasis: string;
  podExecuted: boolean;
  correctlyWitnessed: boolean;
  correctlyCompleted: boolean;
  checksBasis: string;
  groundsForRejection: string;
  groundsBasis: string;
  model: string;
  generatedAt: number;
}

/** Display fields shared by creditor and employee POD status rows. */
export interface PodStatusInfo {
  claimId: Id<"podClaims">;
  status: string;
  recommendation?: string;
  claimedTotal: number;
  admittedAmount?: number;
  rejectedAmount?: number;
  /**
   * True when the claim was matched by name rather than by an explicit link —
   * good enough to report the POD's status against the row, not good enough
   * to treat the row as spoken for.
   */
  matchedByName: boolean;
}

/** Row shape returned by `listPodStatusForLiabilityEntries`. */
export interface PodStatusRow extends PodStatusInfo {
  liabilityEntryId: string;
}

/** Row shape returned by `listPodStatusForEmployeeEntries`. */
export interface EmployeePodStatusRow extends PodStatusInfo {
  employeeEntryId: string;
}

// ---------------------------------------------------------------------------
// Labels & badge colours
// ---------------------------------------------------------------------------

export const POD_STATUS_LABELS: Record<PodClaimStatus, string> = {
  not_requested: "Not requested",
  requested: "Requested",
  received: "Received",
  adjudicated: "Adjudicated",
};

export const POD_STATUS_COLORS: Record<PodClaimStatus, BadgeColor> = {
  not_requested: "gray",
  requested: "amber",
  received: "blue",
  adjudicated: "emerald",
};

export const POD_CREDITOR_TYPE_LABELS: Record<PodCreditorType, string> = {
  unsecured: "Unsecured",
  secured: "Secured",
  priority: "Priority",
  employee: "Employee",
};

export const POD_CREDITOR_TYPE_COLORS: Record<PodCreditorType, BadgeColor> = {
  unsecured: "gray",
  secured: "amber",
  priority: "violet",
  employee: "sky",
};

export const POD_RECOMMENDATION_LABELS: Record<PodRecommendation, string> = {
  admit: "Admit",
  reject: "Reject",
  hold: "Hold",
  more_information: "More information",
};

export const POD_SUPPORTING_DOC_LABELS: Record<PodSupportingDocType, string> = {
  creditor_ledger: "Creditor ledger",
  invoices: "Invoices",
  contracts: "Contracts",
  running_account_statement: "Running account statement",
  other: "Other",
};

// ---------------------------------------------------------------------------
// Proof split (secured / preferential / unsecured)
// ---------------------------------------------------------------------------

/** Creditor types whose proof ranks ahead of ordinary unsecured creditors. */
const PREFERENTIAL_TYPES: ReadonlySet<PodCreditorType> = new Set([
  "priority",
  "employee",
]);

export interface PodProofSplit {
  /** The creditor's own stated value of their security. Null when none. */
  securityHeld: number | null;
  /** Claimed amount ranking as preferential. Null when not a preferred creditor. */
  preferred: number | null;
  /** Claimed amount ranking as ordinary unsecured. Null when none does. */
  unsecured: number | null;
  /** Total amount claimed on the proof. */
  total: number;
}

/**
 * Splits an amount claimed across the classes it ranks in, the way a
 * creditor schedule presents a proof.
 *
 * A secured creditor proves for the shortfall — what they claim less the
 * security they say they hold — so only that balance ranks unsecured; the
 * secured portion is met out of their collateral, not the dividend. A
 * preferential creditor's proof ranks ahead of ordinary unsecured creditors
 * under s556, so it is shown in its own column and not counted again as
 * unsecured. Everything else is ordinary unsecured.
 *
 * Security is only netted off a claim marked secured: a creditor can state
 * particulars of security they hold against a different debt, and taking it
 * off an unsecured proof would understate what ranks for dividend.
 */
export function derivePodProofSplit(claim: {
  creditorType: PodCreditorType;
  claimedTotal: number;
  securityHeldValue?: number;
}): PodProofSplit {
  const total = claim.claimedTotal;
  const isSecured = claim.creditorType === "secured";
  const securityHeld =
    claim.securityHeldValue !== undefined ? claim.securityHeldValue : null;

  if (PREFERENTIAL_TYPES.has(claim.creditorType)) {
    return {
      securityHeld,
      preferred: total > 0 ? total : null,
      unsecured: null,
      total,
    };
  }

  const netOfSecurity =
    isSecured && securityHeld !== null
      ? Math.max(0, total - securityHeld)
      : total;

  return {
    securityHeld,
    preferred: null,
    unsecured: netOfSecurity > 0 ? netOfSecurity : null,
    total,
  };
}

// ---------------------------------------------------------------------------
// Derived adjudication outcome
// ---------------------------------------------------------------------------

export interface PodOutcome {
  label: string;
  color: BadgeColor;
}

/**
 * Derives the display outcome for an adjudicated claim from the
 * recommendation and admitted / rejected split: admitted in full,
 * partially admitted (the iCare case), rejected, hold, or more info.
 */
export function derivePodOutcome(row: {
  status: string;
  recommendation?: string;
  admittedAmount?: number;
  rejectedAmount?: number;
}): PodOutcome | null {
  if (row.status !== "adjudicated") return null;

  if (row.recommendation === "hold") return { label: "On hold", color: "gray" };
  if (row.recommendation === "more_information") {
    return { label: "More info requested", color: "amber" };
  }

  const admitted = row.admittedAmount ?? 0;
  const rejected = row.rejectedAmount ?? 0;

  if (admitted > 0 && rejected > 0) {
    return { label: "Partially admitted", color: "amber" };
  }
  if (admitted > 0) return { label: "Admitted", color: "emerald" };
  return { label: "Rejected", color: "red" };
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

/** `$19,720.50` — always shows cents; adjudication is cent-exact. */
export const fmtMoney = (value: number) =>
  `$${value.toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/**
 * `$19,720.50`, but `$46,900` for a whole-dollar figure.
 *
 * For the claims schedule, where five money columns of `.00` are noise. Cents
 * are still shown whenever they exist, so nothing is rounded away — use the
 * cent-exact `fmtMoney` anywhere a figure is being worked on rather than
 * scanned.
 */
export const fmtMoneyShort = (value: number) =>
  `$${value.toLocaleString("en-AU", {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;

/** Formats an ISO yyyy-mm-dd string as dd/mm/yyyy for display. */
export function fmtDate(iso: string | undefined | null): string {
  if (!iso) return "—";
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return iso;
  return new Date(parsed).toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** Formats an epoch-ms timestamp as dd/mm/yyyy. */
export function fmtTimestamp(ts: number | undefined | null): string {
  if (!ts) return "—";
  return new Date(ts).toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

// ---------------------------------------------------------------------------
// Creditors-table source labels
// ---------------------------------------------------------------------------

/**
 * Mirrors `liabilitySourceValidator` in
 * convex/jobs/summarization/liabilityEntries/schema.ts.
 */
export const LIABILITY_SOURCE_LABELS: Record<string, string> = {
  aged_payables: "Aged payables",
  invoice: "Invoice",
  contract: "Contract",
  ledger: "Ledger",
  bank_statement: "Bank statement",
  director_disclosure: "Director disclosure",
  proof_of_debt: "Proof of Debt",
  other: "Other",
};

export const LIABILITY_NATURE_LABELS: Record<string, string> = {
  trade: "Trade",
  lease: "Lease",
  tax: "Tax",
  utility: "Utility",
  professional_fees: "Prof. fees",
  loan: "Loan",
  insurer: "Insurer",
  other: "Other",
};
